import { Delete, Edit, Visibility } from '@mui/icons-material';
import {
  Checkbox,
  CircularProgress,
  IconButton,
  Radio,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FieldTemplate } from 'api/models';
import { useDrag, useDrop } from 'react-dnd';
import { mergeRefs } from 'react-merge-refs';
import useRequest from 'hooks/useRequest';
import { FieldAttributesOptions } from 'api/models/FieldTemplate';
import { useParams } from 'react-router-dom';
import { GeneralIds, ModelTypes } from 'types/types';
import { Box } from '@mui/system';
import { query } from 'operations';
import { useTranslation } from 'react-i18next';
import useEvaluateConditions from 'hooks/useEvaluateConditions';

interface SelectableTableFieldProps {
  rowIndex: number;
  field: FieldTemplate;
  isError: boolean;
  onEdit?: (field: FieldTemplate) => void;
  onDelete?: (index: number) => void;
  formik: any;
  isLastRow: boolean;
  disabled?: boolean;
  isReadOnly?: boolean;
  externalFieldValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
}

export default function SelectableTableFieldRow({
  rowIndex,
  field,
  isError,
  onEdit,
  onDelete,
  formik,
  isLastRow,
  disabled,
  isReadOnly,
  externalFieldValues,
}: SelectableTableFieldProps) {
  const {
    isRequired,
    label,
    description,
    isMultiSelect,
    options: savedOptions,
    optionsSourceFieldTemplateId,
  } = field.fieldAttributes;
  const { projectId } = useParams<GeneralIds>();
  const { t } = useTranslation();

  const { data: referedOptions, loading } = useRequest<
    FieldAttributesOptions[]
  >(
    async () => {
      if (!optionsSourceFieldTemplateId) return field.fieldAttributes?.options;
      const { data: fields } = await query<FieldTemplate[]>({
        type: ModelTypes.fieldTemplate,
        ids: {
          projectId,
          id: optionsSourceFieldTemplateId,
        },
      });

      const optionField = fields[0];
      return optionField?.fieldAttributes.options;
    },
    {
      dependencies: [
        optionsSourceFieldTemplateId,
        field.fieldAttributes?.options,
      ],
    },
  );

  const options =
    (savedOptions?.length ?? 0) > 0 ? savedOptions : referedOptions ?? [];

  const [{ isOver }, dropRef] = useDrop(() => ({
    accept: 'selectable-table-field',
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
    drop: (droppedField: FieldTemplate) => {
      // TODO: fix this so the items can be reordered
      // const reorderedSelectChildren = produce(children, draft => {
      //   const droppedFieldIndex = draft?.findIndex(
      //     child => child.fieldTemplateId === droppedField.id,
      //   );
      //   const droppedLayoutItem = children?.find(
      //     item => item.fieldTemplateId === droppedField.id,
      //   );
      //   if (droppedFieldIndex !== undefined && droppedFieldIndex !== -1) {
      //     draft?.splice(droppedFieldIndex, 1);
      //   }
      //   if (droppedLayoutItem) {
      //     draft?.splice(rowIndex, 0, droppedLayoutItem);
      //   }
      // });
      // const newLayoutItems = produce(layout, draft => {
      //   set(draft, `${path}.children` || '', reorderedSelectChildren);
      // });
      // setLayout(newLayoutItems);
    },
  }));

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: 'selectable-table-field',
      item: field,
      canDrag: () => Boolean(onEdit && onDelete),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      previewOptions: {
        captureDraggingState: false,
      },
    }),
    [],
  );

  const isVisible = useEvaluateConditions({
    values: { ...externalFieldValues, ...formik?.values },
    conditionGroup: field?.isVisible,
  });

  if (!isVisible && formik) {
    return <></>;
  }

  const getRowBackground = (theme: any) => {
    if (isError) {
      return theme.palette.mode === 'dark'
        ? theme.palette.error.dark
        : theme.palette.error.light;
    }
    if (isOver) {
      return theme.palette.action.hover;
    }
    if (isDragging) {
      return theme.palette.action.selected;
    }
    return undefined;
  };

  return (
    <TableRow
      ref={mergeRefs([dropRef, dragRef])}
      key={`table-row-${field.id}`}
      sx={{
        background: theme => getRowBackground(theme),
        border: theme =>
          isOver ? `2px dashed ${theme.palette.divider}` : undefined,
      }}
    >
      <TableCell
        sx={{
          borderBottom: isLastRow ? 'none' : undefined,
        }}
      >
        {label} {isRequired && '*'}
        {description && (
          <Typography
            variant="subtitle2"
            sx={{ color: theme => theme.palette.text.secondary }}
          >
            {description}
          </Typography>
        )}
      </TableCell>
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            backdropFilter: 'blur(10px)',
            cursor: 'not-allowed',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        options?.map((option, index) => {
          return (
            <TableCell
              sx={{
                borderBottom: isLastRow ? 'none' : undefined,
              }}
              align="center"
              key={`selectable-table-field-cell-${field.id}-${option.label}-${option.value}`}
            >
              {isMultiSelect ? (
                <Checkbox
                  checked={formik?.values[field.id].includes(option.value)}
                  onClick={e => {
                    if (isReadOnly) return;
                    const value = formik?.values[field.id];
                    if (value?.includes(option.value)) {
                      formik?.setFieldValue(
                        field.id,
                        value?.filter((v: any) => v !== option.value),
                      );
                    } else {
                      formik?.setFieldValue(field.id, [...value, option.value]);
                    }
                  }}
                />
              ) : (
                <Radio
                  checked={formik?.values[field.id] === option.value}
                  onClick={e => {
                    if (isReadOnly) return;
                    if (option.value === formik?.values[field.id]) {
                      return formik?.setFieldValue(field.id, '');
                    }
                    formik?.setFieldValue(field.id, option.value);
                  }}
                />
              )}
            </TableCell>
          );
        })
      )}
      {!loading && onEdit && onDelete && (
        <TableCell
          align="right"
          sx={{
            borderBottom: isLastRow ? 'none' : undefined,
          }}
        >
          <Tooltip title={disabled ? t('general.view') : t('general.edit')}>
            <IconButton size="small" onClick={() => onEdit(field)}>
              {disabled ? (
                <Visibility fontSize="small" />
              ) : (
                <Edit fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <IconButton
            size="small"
            onClick={() => onDelete(rowIndex)}
            disabled={disabled}
          >
            <Tooltip title={t('general.delete')}>
              <Delete fontSize="small" />
            </Tooltip>
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
}
